import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import './EditTeamModal.css';

interface TeamMember {
  id?: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
  username: string;
  role: string;
  can_edit_buoni?: boolean;
  can_delete_buoni?: boolean;
  team_members?: TeamMember[];
  teamMembers?: TeamMember[];
}

interface EditTeamModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  team: Team | null;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({ show, onHide, onSuccess, team }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [canEditBuoni, setCanEditBuoni] = useState(false);
  const [canDeleteBuoni, setCanDeleteBuoni] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (team) {
      setUsername(team.username || '');
      setPassword('');
      setCanEditBuoni(!!team.can_edit_buoni);
      setCanDeleteBuoni(!!team.can_delete_buoni);
    }
  }, [team]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!team) return;

    if (password && password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const updateData: any = {
        username: username.trim(),
        can_edit_buoni: canEditBuoni,
        can_delete_buoni: canDeleteBuoni,
      };
      if (password) updateData.password = password;

      const response = await fetch(`${API_URL}/admin/teams/${team.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ');
          throw new Error(errorMessages || data.message || 'Errore durante l\'aggiornamento');
        }
        throw new Error(data.message || 'Errore durante l\'aggiornamento');
      }

      setPassword('');
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || 'Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  if (!team) return null;

  return (
    <Modal show={show} onHide={onHide} centered className="edit-user-modal">
      <div className="edit-user-modal-box">
        <div className="edit-user-modal-inner">
          <div className="edit-user-modal-header">
            <h2 className="edit-user-modal-title">Modifica Utente</h2>
            <button
              type="button"
              className="edit-user-modal-close"
              onClick={onHide}
              aria-label="Chiudi"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 2l12 12M14 2L2 14" />
              </svg>
            </button>
          </div>

          <Form onSubmit={handleSubmit} className="edit-user-form">
            <div className="edit-user-form-body">
              {error && <Alert variant="danger" className="edit-user-alert">{error}</Alert>}

              <div className="edit-user-field">
                <label className="edit-user-label">Nome Utente</label>
                <Form.Control
                  type="text"
                  placeholder="Nome"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="edit-user-input"
                  required
                />
              </div>

              <div className="edit-user-field">
                <label className="edit-user-label">Nuova Password</label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="edit-user-input"
                  minLength={8}
                />
              </div>

              <div className="edit-user-permessi">
                <label className="edit-user-label edit-user-permessi-title">Permessi</label>
                <label className="edit-user-permessi-row">
                  <Form.Check
                    type="checkbox"
                    checked={canEditBuoni}
                    onChange={() => setCanEditBuoni(!canEditBuoni)}
                    className="edit-user-checkbox-input"
                  />
                  <span className="edit-user-permessi-label">Modifica Buono</span>
                </label>
                <label className="edit-user-permessi-row">
                  <Form.Check
                    type="checkbox"
                    checked={canDeleteBuoni}
                    onChange={() => setCanDeleteBuoni(!canDeleteBuoni)}
                    className="edit-user-checkbox-input"
                  />
                  <span className="edit-user-permessi-label">Elimina Buono</span>
                </label>
              </div>
            </div>

            <div className="edit-user-footer">
              <Button type="button" variant="light" className="edit-user-btn-annulla" onClick={onHide} disabled={loading}>
                Annulla
              </Button>
              <Button type="submit" className="edit-user-btn-submit" disabled={loading}>
                {loading ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default EditTeamModal;
